import "./_style.less";
import { ReactNode } from "react";
import { Descriptions } from "antd";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { snakeCaseToTitleCase } from "../../utils/helpers";
import { LeadActivityTimelineProps } from "./interface";

const LeadActivityTimeline = ({ activity }: LeadActivityTimelineProps) => {
  const { t } = useTranslation();

  const renderDescriptions = (items: { label: string; value?: ReactNode }[]) => {
    const visible = items.filter((it) => it.value !== undefined && it.value !== null && it.value !== "");
    if (!visible.length) return null;

    return (
      <Descriptions className="custom-descriptions" column={1} bordered size="small">
        {visible.map((it, idx) => (
          <Descriptions.Item key={idx} label={t(it.label)}>
            {it.value}
          </Descriptions.Item>
        ))}
      </Descriptions>
    );
  };

  switch (activity.status) {
    case "QUALIFIED_OPPORTUNITY":
      return renderDescriptions([
        {
          label: "common.form.painPoint.field",
          value: activity.pain_point
        },
        {
          label: "common.form.need.field",
          value: activity.need
        },
        {
          label: "common.form.authority.label",
          value: activity.authority
        },
        {
          label: "common.form.timeline.label",
          value: activity.timeline?.label
        },
        {
          label: "common.form.estimatedCloseDate.label",
          value: activity.estimated_close_date
            ? moment(activity.estimated_close_date).format("DD MMM YYYY")
            : undefined,
        },
        {
          label: "common.form.internalNote.label",
          value: activity.qualified_opportunity_internal_note
        },
      ]);

    case "SOLUTION_FIT":
      return renderDescriptions([
        {
          label: "common.form.metric.label",
          value: activity.metric
        },
        {
          label: "common.form.decision.label",
          value: activity.decision
        },
        {
          label: "common.form.internalNote.label",
          value: activity.solution_fit_internal_note
        },
      ]);

    case "VENDOR_OF_CHOICE":
      return renderDescriptions([
        {
          label: "common.form.internalInfluence.label",
          value: activity.internal_influence?.name
        },
        {
          label: "common.form.decisionMaker.label",
          value: Array.isArray(activity.decision_make) && activity.decision_make.length
            ? activity.decision_make.map((dm) => [dm.full_name, dm.job_title, dm.email, dm.phone]
              .filter(Boolean)
              .join(", "))
            : undefined,
        },
        {
          label: "common.form.internalNote.label",
          value: activity.vendor_of_choice_internal_note
        },
      ]);

    case "TERMS_AGREED":
      return renderDescriptions([
        {
          label: "common.form.decisionProcess.label",
          value: activity.decision_process
        },
        {
          label: "common.form.proposalAttachment.label",
          value: activity.proposal_attachment?.url
            ? (<a href={activity.proposal_attachment.url} target="_blank" rel="noopener noreferrer">{activity.proposal_attachment.name || "View File"}</a>)
            : activity.proposal_attachment?.name,
        },
        {
          label: "common.form.note.label",
          value: activity.terms_agreed_note
        },
      ]);

    case "CLOSED_WON":
      return renderDescriptions([
        {
          label: "common.form.contractAttachment.field",
          value: activity.contract_attachment?.url
            ? (<a href={activity.contract_attachment.url} target="_blank">{activity.contract_attachment.name || "View File"}</a>)
            : undefined,
        },
        {
          label: "common.form.note.label",
          value: activity.closed_won_note
        },
      ]);

    case "CLOSED_LOST":
      return renderDescriptions([
        {
          label: "common.form.lostReason.label",
          value: snakeCaseToTitleCase(activity.lost_reason ?? "")
        },
        {
          label: "common.form.lostAnalysis.field",
          value: activity.lost_analysis
        },
      ]);

    default:
      return null;
  }
};

export default LeadActivityTimeline;
